

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8A04C]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1917] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary — Antique Gold
        default:
          'bg-[#B8A04C] text-[#1C1917] hover:bg-[#C9B05D] active:bg-[#A8903C]',
        // Destructive — Oxblood
        destructive:
          'bg-[#8B3A3A] text-[#F5F0E8] hover:bg-[#9B4A4A] active:bg-[#7B2A2A]',
        // Outline — warm border
        outline:
          'border-2 border-[#3E3A34] bg-transparent text-[#F5F0E8] hover:border-[#B8A04C] hover:text-[#B8A04C]',
        // Secondary — Surface background
        secondary:
          'bg-[#272320] text-[#F5F0E8] hover:bg-[#33302B]',
        // Ghost — Minimal
        ghost:
          'text-[#B8B0A0] hover:bg-[#272320] hover:text-[#F5F0E8]',
        // Link
        link:
          'text-[#B8A04C] underline-offset-4 hover:underline',
        // Success — Verdigris
        success:
          'bg-[#5B8A72] text-[#F5F0E8] hover:bg-[#6B9A82] active:bg-[#4B7A62]',
        // Rarity variants
        prime:
          'bg-[#B8A04C] text-[#1C1917] hover:bg-[#C9B05D] shadow-lg shadow-[#B8A04C]/20 active:bg-[#A8903C]',
        legendary:
          'bg-[#B8A04C] text-[#1C1917] hover:bg-[#C9B05D] shadow-lg shadow-[#B8A04C]/20',
        elite:
          'bg-[#8B5CF6] text-[#F5F0E8] hover:bg-[#7C3AED] shadow-lg shadow-[#8B5CF6]/20',
        rare:
          'bg-[#5B8A72] text-[#F5F0E8] hover:bg-[#6B9A82] shadow-lg shadow-[#5B8A72]/20',
        // Currency variants
        coins:
          'bg-gradient-to-r from-[#B8A04C] to-[#C9B05D] text-[#1C1917]',
        gems:
          'bg-gradient-to-r from-[#5B8A72] to-[#2D4A3E] text-[#F5F0E8]',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-xl px-8 text-base',
        xl: 'h-14 rounded-xl px-10 text-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading...</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
